"use client";

import { primaryColor, secondaryColor } from "@/lib/theme";
import { sendUserRating } from "@/services/userRating";
import {
  hasInteractedWithUserRating,
  saveUserRatingRecord,
  shouldShowUserRating,
} from "@/utils/userRatingStorage";
import { AnimatePresence, motion } from "framer-motion";
import { Star, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Fim do fluxo de pagamento — chegar aqui já é o gatilho. */
const PAYMENT_SUCCESS_PATH = "/encomendas/pagamento/sucesso";

/**
 * Rotas de um segmento sob /encomendas que não são o [order_id]. Sem essa
 * lista, qualquer uma delas passaria pelo teste do detalhe do pedido.
 */
const ENCOMENDAS_STATIC_SEGMENTS = new Set([
  "dados",
  "envio",
  "envios",
  "pacotes",
  "pagamento",
  "rastreio",
  "web-view",
]);

/** true em /encomendas/<order_id> — a tela de detalhe do pedido. */
const isOrderDetailsPath = (pathname: string): boolean => {
  const segment = /^\/encomendas\/([^/]+)\/?$/.exec(pathname)?.[1];
  return segment !== undefined && !ENCOMENDAS_STATIC_SEGMENTS.has(segment);
};

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Espera depois do load completo da página antes de exibir a barra. */
const SHOW_DELAY_MS = 3000;

/** A partir dessa nota não pedimos justificativa. */
const COMMENT_THRESHOLD = 8;

/** Tempo do pop das estrelas antes de trocar de etapa. */
const STARS_ANIMATION_MS = 700;

/** Tempo que o agradecimento fica na tela antes do fechamento. */
const THANKS_DURATION_MS = 1600;

const ACTIVE_STAR_STROKE = "#1D2530";
const IDLE_STAR_STROKE = "#CBD3DE";

type Phase = "rating" | "comment" | "thanks";

/** Crossfade entre as etapas — só opacidade e deslocamento, nunca layout. */
const STEP_TRANSITION = { duration: 0.26, ease: [0.16, 1, 0.3, 1] } as const;

interface UserRatingBarProps {
  /** Nome exibido na pergunta. */
  productName?: string;
  /** Callback opcional pra enviar a nota a um backend/analytics. */
  onRate?: (score: number) => void;
  /** Callback opcional pra enviar a justificativa das notas baixas. */
  onComment?: (score: number, comment: string) => void;
}

const UserRatingBar = ({
  productName = "Embarca Log",
  onRate,
  onComment,
}: UserRatingBarProps) => {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("rating");
  const [score, setScore] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [eligible, setEligible] = useState(false);
  const evaluatedPathRef = useRef<string | null>(null);
  const stageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);

  // localStorage só existe no client — nada é decidido no SSR, senão o markup
  // do servidor divergiria do primeiro render do browser.
  useEffect(() => setMounted(true), []);

  // Gatilhos: o fim do fluxo de pagamento, sempre; e o detalhe do pedido, só
  // para quem nunca respondeu nem fechou a barra. Cada rota é avaliada uma vez
  // por navegação, senão o teste rodaria a cada re-render.
  useEffect(() => {
    if (!mounted) return;
    if (evaluatedPathRef.current === pathname) return;
    evaluatedPathRef.current = pathname;

    if (pathname === PAYMENT_SUCCESS_PATH) {
      setEligible(true);
    } else if (isOrderDetailsPath(pathname)) {
      setEligible(!hasInteractedWithUserRating());
    } else {
      setEligible(false);
    }
  }, [mounted, pathname]);

  useEffect(() => {
    if (!eligible) return;

    let showTimer: ReturnType<typeof setTimeout> | null = null;

    // Só entra em cena 3s depois do load completo (imagens, fontes, scripts),
    // pra não competir com o conteúdo principal da página.
    const scheduleShow = () => {
      showTimer = setTimeout(() => {
        if (shouldShowUserRating()) setOpen(true);
      }, SHOW_DELAY_MS);
    };

    if (document.readyState === "complete") {
      scheduleShow();
    } else {
      window.addEventListener("load", scheduleShow);
    }

    return () => {
      window.removeEventListener("load", scheduleShow);
      if (showTimer) clearTimeout(showTimer);
    };
  }, [eligible]);

  useEffect(() => {
    return () => {
      if (stageTimer.current) clearTimeout(stageTimer.current);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  // As etapas ficam todas montadas (pra caixa não mudar de tamanho), então o
  // foco do textarea é dado na mão quando a etapa entra.
  useEffect(() => {
    if (phase === "comment") commentRef.current?.focus();
  }, [phase]);

  const showThanks = useCallback(() => {
    setPhase("thanks");
    dismissTimer.current = setTimeout(() => setOpen(false), THANKS_DURATION_MS);
  }, []);

  // Grava e envia a nota com o comentário digitado até aqui. Serve ao botão
  // "Enviar avaliação" e ao fechamento durante a etapa do comentário.
  const submitRating = useCallback(() => {
    if (score === null) return;
    const trimmed = comment.trim();
    void sendUserRating(saveUserRatingRecord("rated", score, trimmed || null));
    if (trimmed) onComment?.(score, trimmed);
  }, [score, comment, onComment]);

  const handleClose = useCallback(() => {
    if (score === null) {
      void sendUserRating(saveUserRatingRecord("dismissed"));
    } else if (phase === "comment") {
      // Fechar aqui vale como enviar: a nota é mantida e o texto já digitado
      // vai junto. Sem esse caso, o "dismissed" apagaria a nota do usuário.
      submitRating();
    }
    setOpen(false);
  }, [score, phase, submitRating]);

  const handleSelect = (value: number) => {
    // Ignora cliques repetidos enquanto a animação roda.
    if (score !== null) return;
    setScore(value);
    void sendUserRating(saveUserRatingRecord("rated", value));
    onRate?.(value);

    // Deixa o pop das estrelas terminar antes de trocar o conteúdo: nota alta
    // agradece e fecha, nota baixa abre o campo de justificativa.
    stageTimer.current = setTimeout(() => {
      if (value >= COMMENT_THRESHOLD) showThanks();
      else setPhase("comment");
    }, STARS_ANIMATION_MS);
  };

  const handleSubmitComment = () => {
    // Comentário é opcional: sem texto, só confirma a nota já gravada.
    submitRating();
    showThanks();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && phase !== "thanks") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, phase, handleClose]);

  if (!mounted) return null;

  const highlighted = hovered ?? score ?? 0;

  // A etapa inativa sai da altura (mas continua definindo a largura): a caixa
  // só muda na vertical, e o `layout` do card anima essa mudança.
  const stepProps = (active: boolean) => ({
    animate: { opacity: active ? 1 : 0, y: active ? 0 : 6 },
    transition: STEP_TRANSITION,
    "aria-hidden": !active,
    style: { pointerEvents: active ? ("auto" as const) : ("none" as const) },
    className: `col-start-1 row-start-1 flex flex-col justify-center ${
      active ? "h-full" : "h-0 overflow-hidden"
    }`,
  });

  const bar = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="user-rating-bar"
          role="dialog"
          aria-label="Avaliação do produto"
          className="fixed inset-x-0 bottom-0 z-[180] flex justify-center px-4 pb-4 pointer-events-none"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{
            type: "spring",
            damping: 28,
            stiffness: 360,
            mass: 0.85,
          }}
        >
          <motion.div
            layout
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto relative w-full max-w-[40rem] overflow-hidden md:w-auto md:max-w-[calc(100vw-2rem)] rounded-card border border-line bg-paper px-4 py-4 shadow-float sm:px-6 sm:py-5"
          >
            {/* Fechar fica fora do fluxo: não entra na altura que a caixa anima */}
            <motion.button
              id="btn-user-rating-close"
              type="button"
              aria-label="Fechar"
              tabIndex={phase === "thanks" ? -1 : 0}
              onClick={handleClose}
              className="absolute right-2 top-2 rounded-full p-2 text-mist transition-colors hover:bg-ground hover:text-ink sm:right-3 sm:top-3"
              animate={{ opacity: phase === "thanks" ? 0 : 1 }}
              transition={STEP_TRANSITION}
              aria-hidden={phase === "thanks"}
              style={{ pointerEvents: phase === "thanks" ? "none" : "auto" }}
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </motion.button>

            {/*
              Todas as etapas ocupam a mesma célula do grid: a largura da caixa é
              o máximo entre elas (fixa), só a altura muda — e animada.
            */}
            <div className="grid">
              {/* Etapa 1 — nota */}
              <motion.div {...stepProps(phase === "rating")}>
                <h2 className="pr-9 text-body font-bold leading-snug text-ink md:whitespace-nowrap sm:text-body">
                  {`Qual a probabilidade de você recomendar o ${productName} para alguém?`}
                </h2>

                {/* Escala de 1 a 10 */}
                <div className="mt-4 flex w-full items-stretch justify-between md:justify-center">
                  {SCORES.map((value) => {
                    const active = value <= highlighted;
                    const chosen = score !== null && value <= score;

                    return (
                      <button
                        key={value}
                        id={`btn-user-rating-${value}`}
                        type="button"
                        aria-label={`Nota ${value} de 10`}
                        disabled={score !== null}
                        tabIndex={phase === "rating" ? 0 : -1}
                        onMouseEnter={() => setHovered(value)}
                        onMouseLeave={() => setHovered(null)}
                        onFocus={() => setHovered(value)}
                        onBlur={() => setHovered(null)}
                        onClick={() => handleSelect(value)}
                        className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-chip py-1.5 transition-colors enabled:hover:bg-[#F7F8FA] disabled:cursor-default md:w-10 md:flex-none"
                      >
                        <motion.span
                          className="block w-full max-w-[26px] sm:max-w-7 md:max-w-[30px]"
                          animate={
                            chosen ? { scale: [1, 1.35, 1] } : { scale: 1 }
                          }
                          whileHover={
                            score === null ? { scale: 1.15 } : undefined
                          }
                          whileTap={score === null ? { scale: 0.85 } : undefined}
                          transition={{
                            duration: 0.34,
                            delay: chosen ? value * 0.03 : 0,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                        >
                          <Star
                            className="h-auto w-full transition-colors"
                            strokeWidth={1.75}
                            style={{
                              color: active
                                ? ACTIVE_STAR_STROKE
                                : IDLE_STAR_STROKE,
                              fill: active ? secondaryColor : "transparent",
                            }}
                          />
                        </motion.span>
                        <span
                          className={`text-micro font-bold leading-none transition-colors ${
                            active ? "text-ink" : "text-mist"
                          }`}
                        >
                          {value}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-2 flex items-center justify-between text-micro text-slate">
                  <span>Não recomendaria</span>
                  <span>Recomendaria sem dúvida</span>
                </div>
              </motion.div>

              {/* Etapa 2 — justificativa das notas baixas */}
              <motion.div {...stepProps(phase === "comment")}>
                <h2 className="pr-9 text-body font-bold leading-snug text-ink sm:text-body">
                  Compartilhe sua experiência com a plataforma para melhorarmos no futuro
                </h2>

                <div className="mt-3 flex flex-col gap-2">
                  <textarea
                    id="input-user-rating-comment"
                    ref={commentRef}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    rows={2}
                    maxLength={500}
                    disabled={phase !== "comment"}
                    tabIndex={phase === "comment" ? 0 : -1}
                    aria-label="Comentário (opcional)"
                    placeholder="Comentário (opcional)"
                    className="focus-secondary min-w-0 flex-1 resize-none rounded-control border border-line bg-white px-3 py-2.5 text-body font-semibold text-ink outline-none transition-colors placeholder:font-normal placeholder:text-mist"
                  />
                  <button
                    id="btn-user-rating-submit"
                    type="button"
                    disabled={phase !== "comment"}
                    tabIndex={phase === "comment" ? 0 : -1}
                    onClick={handleSubmitComment}
                    className="h-11 w-full shrink-0 self-end rounded-control px-5 text-body font-bold text-white transition-opacity hover:opacity-90 sm:w-auto"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Enviar avaliação
                  </button>
                </div>
              </motion.div>

              {/* Etapa 3 — agradecimento: a caixa encolhe até só ele sobrar */}
              <motion.div {...stepProps(phase === "thanks")}>
                <p className="text-center text-body font-bold text-ink sm:text-body">
                  Obrigado pelo seu feedback!
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;
  return createPortal(bar, document.body);
};

export default UserRatingBar;
